import { supabase } from '../lib/supabase';
import type { CollectorTier, DistrictPath, ShowcaseType } from '../lib/collectorCultureSystem';

export type CollectorSnapshot = {
  collectorScore: number;
  collectorTier: CollectorTier;
  cultureMemberXp: number;
  cultureMemberLevel: number;
  trendHunterPoints: number;
  districtPath: DistrictPath;
  purchases: number;
  closetCount: number;
  collectionValue: number;
};

export type CollectionShowcase = {
  id: string;
  user_id: string;
  title: string;
  showcase_type: ShowcaseType;
  description: string;
  cover_image: string | null;
  item_ids: string[];
  is_public: boolean;
  view_count: number;
  like_count: number;
  created_at: string;
};

export type PassportStamp = {
  id: string;
  stamp_type: string;
  title: string;
  subtitle: string | null;
  meta: Record<string, unknown>;
  earned_at: string;
};

export type CultureMissionCompletion = {
  mission_key: string;
  mission_date: string;
  progress: number;
  target: number;
  xp_awarded: number;
  completed_at: string | null;
};

export type LeaderboardRow = {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  rank: number;
  score: number;
  tier?: string;
  level?: number;
  trendPoints?: number;
  badge?: string | null;
};

export type HuntResponse = {
  id: string;
  seller_id: string;
  seller_username: string;
  seller_avatar: string | null;
  price: number;
  message: string | null;
  status: string;
  created_at: string;
};

export const collectorCultureService = {
  async refreshProfile(userId?: string): Promise<CollectorSnapshot | null> {
    const { data, error } = await supabase.rpc('refresh_collector_profile', {
      p_user_id: userId ?? undefined,
    });
    if (error || !data?.ok) return null;
    return {
      collectorScore: data.collector_score ?? 0,
      collectorTier: (data.collector_tier ?? 'bronze') as CollectorTier,
      cultureMemberXp: data.culture_member_xp ?? 0,
      cultureMemberLevel: data.culture_member_level ?? 1,
      trendHunterPoints: data.trend_hunter_points ?? 0,
      districtPath: (data.district_path ?? 'member') as DistrictPath,
      purchases: data.purchases ?? 0,
      closetCount: data.closet_count ?? 0,
      collectionValue: Number(data.collection_value ?? 0),
    };
  },

  async getCollectorLeaderboard(limit = 25): Promise<LeaderboardRow[]> {
    const { data, error } = await supabase.rpc('get_collector_leaderboard', { p_limit: limit });
    if (error || !data) return [];
    return (data as any[]).map((r) => ({
      user_id: r.user_id,
      username: r.username,
      display_name: r.display_name,
      avatar_url: r.avatar_url,
      rank: Number(r.rank),
      score: r.collector_score ?? 0,
      tier: r.collector_tier,
    }));
  },

  async getCultureLeaderboard(limit = 25): Promise<LeaderboardRow[]> {
    const { data, error } = await supabase.rpc('get_culture_leaderboard', { p_limit: limit });
    if (error || !data) return [];
    return (data as any[]).map((r) => ({
      user_id: r.user_id,
      username: r.username,
      display_name: r.display_name,
      avatar_url: r.avatar_url,
      rank: Number(r.rank),
      score: r.culture_member_xp ?? 0,
      level: r.culture_member_level,
      trendPoints: r.trend_hunter_points,
    }));
  },

  async claimMission(missionKey: string, xp = 10, target = 1) {
    return supabase.rpc('claim_culture_mission', {
      p_mission_key: missionKey,
      p_xp: xp,
      p_target: target,
    });
  },

  async recordMissionProgress(missionKey: string, increment = 1, xp = 10, target = 1) {
    return supabase.rpc('record_culture_mission_progress', {
      p_mission_key: missionKey,
      p_increment: increment,
      p_xp: xp,
      p_target: target,
    });
  },

  async getActiveSeason() {
    const { data, error } = await supabase
      .from('collector_seasons')
      .select('*')
      .eq('is_active', true)
      .order('starts_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    return { data, error };
  },

  async getSeasonLeaderboard(seasonId?: string, limit = 25) {
    const { data, error } = await supabase.rpc('get_collector_season_leaderboard', {
      p_season_id: seasonId ?? null,
      p_limit: limit,
    });
    if (error || !data) return [];
    return (data as any[]).map((r) => ({
      user_id: r.user_id,
      username: r.username,
      display_name: r.display_name,
      avatar_url: r.avatar_url,
      rank: Number(r.rank),
      score: r.collector_score ?? 0,
      purchases: r.purchases ?? 0,
      vaultValue: Number(r.vault_value ?? 0),
      cultureXp: r.culture_xp ?? 0,
      badge: r.badge_awarded,
    }));
  },

  async listHuntResponses(requestId: string): Promise<HuntResponse[]> {
    const { data, error } = await supabase.rpc('list_hunt_responses', { p_request_id: requestId });
    if (error || !data) return [];
    return (data as any[]).map((r) => ({
      id: r.id,
      seller_id: r.seller_id,
      seller_username: r.seller_username,
      seller_avatar: r.seller_avatar,
      price: Number(r.price),
      message: r.message,
      status: r.status,
      created_at: r.created_at,
    }));
  },

  async acceptHuntResponse(responseId: string) {
    return supabase.rpc('accept_hunt_response', { p_response_id: responseId });
  },

  async getPublicCollectorBadge(userId: string) {
    const { data, error } = await supabase.rpc('get_public_collector_badge', { p_user_id: userId });
    if (error || !data?.ok) return null;
    return data as {
      collector_score: number;
      collector_tier: string;
      culture_level: number;
      trend_hunter_points: number;
      season_badge: string | null;
      season_rank: number | null;
    };
  },

  async getMySeasonBadge(userId: string, seasonId?: string) {
    const seasonRes = seasonId
      ? { data: { id: seasonId } }
      : await this.getActiveSeason();
    const sid = seasonRes.data?.id;
    if (!sid) return null;
    const { data } = await this.getMySeasonStanding(userId, sid);
    return data?.badge_awarded ?? null;
  },

  async getMySeasonStanding(userId: string, seasonId: string) {
    const { data, error } = await supabase
      .from('collector_season_standings')
      .select('*')
      .eq('user_id', userId)
      .eq('season_id', seasonId)
      .maybeSingle();
    return { data, error };
  },

  async awardAchievement(key: string, title: string, description?: string, emoji = '🏆') {
    return supabase.rpc('award_collector_achievement', {
      p_achievement_key: key,
      p_title: title,
      p_description: description ?? null,
      p_emoji: emoji,
    });
  },

  async addPassportStamp(type: string, title: string, subtitle?: string, meta?: Record<string, unknown>) {
    return supabase.rpc('add_passport_stamp', {
      p_stamp_type: type,
      p_title: title,
      p_subtitle: subtitle ?? null,
      p_meta: meta ?? {},
    });
  },

  async listShowcases(userId: string): Promise<CollectionShowcase[]> {
    const { data, error } = await supabase
      .from('collection_showcases')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) return [];
    return (data || []) as CollectionShowcase[];
  },

  async listPublicShowcases(userId: string): Promise<CollectionShowcase[]> {
    const { data, error } = await supabase
      .from('collection_showcases')
      .select('*')
      .eq('user_id', userId)
      .eq('is_public', true)
      .order('created_at', { ascending: false });
    if (error) return [];
    return (data || []) as CollectionShowcase[];
  },

  async createShowcase(input: {
    user_id: string;
    title: string;
    showcase_type: ShowcaseType;
    description?: string;
    item_ids?: string[];
    cover_image?: string;
    is_public?: boolean;
  }) {
    const { data, error } = await supabase
      .from('collection_showcases')
      .insert({
        user_id: input.user_id,
        title: input.title,
        showcase_type: input.showcase_type,
        description: input.description ?? '',
        item_ids: input.item_ids ?? [],
        cover_image: input.cover_image ?? null,
        is_public: input.is_public ?? true,
      })
      .select()
      .single();
    return { data: data as CollectionShowcase | null, error };
  },

  async deleteShowcase(id: string) {
    return supabase.from('collection_showcases').delete().eq('id', id);
  },

  async listPassportStamps(userId: string): Promise<PassportStamp[]> {
    const { data, error } = await supabase
      .from('district88_passport_stamps')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });
    if (error) return [];
    return (data || []) as PassportStamp[];
  },

  async listTodayMissions(userId: string): Promise<CultureMissionCompletion[]> {
    const today = new Date().toISOString().slice(0, 10);
    const { data, error } = await supabase
      .from('culture_mission_completions')
      .select('mission_key, mission_date, progress, target, xp_awarded, completed_at')
      .eq('user_id', userId)
      .eq('mission_date', today);
    if (error) return [];
    return (data || []) as CultureMissionCompletion[];
  },

  async listGrailHunts() {
    const { data, error } = await supabase
      .from('buyer_requests')
      .select('*, buyer:profiles!buyer_id(username, avatar_url, collector_tier)')
      .eq('status', 'open')
      .in('request_kind', ['grail_hunt', 'bounty'])
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) return [];
    return data || [];
  },

  async createGrailHunt(input: {
    buyer_id: string;
    title: string;
    category?: string;
    size?: string;
    budget?: number;
    body?: string;
    request_kind?: 'grail_hunt' | 'bounty';
    finder_reward?: number;
  }) {
    const { data, error } = await supabase
      .from('buyer_requests')
      .insert({
        ...input,
        request_kind: input.request_kind ?? 'grail_hunt',
        status: 'open',
      })
      .select('*, buyer:profiles!buyer_id(username, avatar_url)')
      .single();
    return { data, error };
  },

  async trackTrendSave(userId: string, listingId: string) {
    const { error } = await supabase.from('trend_hunter_saves').upsert(
      { user_id: userId, listing_id: listingId, saved_at: new Date().toISOString() },
      { onConflict: 'user_id,listing_id', ignoreDuplicates: true },
    );
    return { error };
  },

  async syncPurchasesToVault(userId?: string) {
    const { data, error } = await supabase.rpc('sync_buyer_purchases_to_vault', {
      p_user_id: userId ?? undefined,
    });
    return { data, error, synced: (data as { synced?: number })?.synced ?? 0 };
  },

  async awardMilestones(userId?: string) {
    return supabase.rpc('award_collector_milestones', { p_user_id: userId ?? undefined });
  },

  async celebratePurchase(userId?: string) {
    const sync = await this.syncPurchasesToVault(userId);
    await this.awardMilestones(userId);
    await this.addPassportStamp('purchase', 'New pickup secured', 'Added to your collection vault');
    const snap = await this.refreshProfile(userId);
    return { ...sync, snapshot: snap };
  },
};
